import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle, AlertTriangle } from "lucide-react";

export default function HeroSection() {
  const { t } = useTranslation();

  const problems = [
    t("hero.problem1"),
    t("hero.problem2"),
    t("hero.problem3"),
    t("hero.problem4")
  ];

  const benefits = [
    t("hero.benefit1"),
    t("hero.benefit2"),
    t("hero.benefit3")
  ];

  const scrollToContact = () => {
    const element = document.querySelector("#kontakti");
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: "smooth" });
    }
  };

  const scrollToHowItWorks = () => {
    const element = document.querySelector("#ka-tas-darbojas");
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: "smooth" });
    }
  };

  return (
    <section 
      className="relative min-h-screen flex items-center hero-pattern pt-20"
      data-testid="hero-section"
    >
      <div className="section-container">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content */}
          <div className="animate-slide-up">
            <span className="inline-block bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-6">
              {t("hero.badge")}
            </span>
            
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-foreground leading-tight mb-6"
              style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700 }}
            >
              {t("hero.title1")}
              <span className="text-primary">{t("hero.title2")}</span>
            </h1>
            
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-8 max-w-xl">
              {t("hero.description")}
            </p>

            {/* Benefits list */}
            <div className="flex flex-wrap gap-4 mb-10">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center gap-2 text-foreground">
                  <CheckCircle className="w-5 h-5 text-primary" />
                  <span className="font-medium">{benefit}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={scrollToContact}
                className="btn-primary group"
                data-testid="hero-cta-primary"
              >
                {t("hero.ctaPrimary")}
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                onClick={scrollToHowItWorks}
                variant="outline"
                className="btn-secondary"
                data-testid="hero-cta-secondary"
              >
                {t("hero.ctaSecondary")}
              </Button>
            </div>
          </div>

          {/* Image */}
          <div className="relative animate-fade-in">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl img-zoom">
              <img 
                src="https://customer-assets.emergentagent.com/job_senior-care-10/artifacts/yt93q1sx_DSCF6804-Enhanced-NR-Edit-1.jpg"
                alt="Attālināta senioru uzraudzības sistēma — viedierīces drošībai un aprūpei mājās Latvijā"
                className="w-full h-[500px] object-cover"
              />
              {/* Overlay card */}
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm rounded-2xl p-6 shadow-lg">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {t("hero.companyName")}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {t("hero.companySub")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-accent/20 rounded-full blur-2xl" />
            <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-primary/10 rounded-full blur-3xl" />
          </div>
        </div>

        {/* Problems Section */}
        <div className="mt-20 bg-white rounded-3xl p-8 md:p-12 shadow-sm border border-border">
          <div className="grid md:grid-cols-2 gap-8 items-start">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-accent/20 rounded-xl flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-accent" />
                </div>
                <h2 
                  className="text-xl font-bold text-foreground"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  {t("hero.problemsTitle")}
                </h2>
              </div>
              <p className="text-muted-foreground mb-6">
                {t("hero.problemsDesc")}
              </p>
            </div>
            <div className="space-y-3">
              {problems.map((problem, index) => (
                <div key={index} className="flex items-start gap-3 p-3 bg-secondary/50 rounded-xl">
                  <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-primary">{index + 1}</span>
                  </div>
                  <span className="text-foreground/80">{problem}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
